import React from "react";
import {
  Cpu,
  LineChart,
  ShieldCheck,
  Layers,
  Workflow,
  Sparkles,
  ArrowRight,
  Download,
  Quote,
} from "lucide-react";
import Nav from "@/components/site/Nav";
import Footer from "@/components/site/Footer";
import Monogram from "@/components/brand/Monogram";
import Spark from "@/components/brand/Spark";
import SectionLabel from "@/components/site/SectionLabel";
import Reveal from "@/components/site/Reveal";
import LeadForm from "@/components/site/LeadForm";
import ContactForm from "@/components/site/ContactForm";
import PullQuote from "@/components/site/PullQuote";
import StatsStrip from "@/components/site/StatsStrip";
import EditorialBreak from "@/components/site/EditorialBreak";
import Ornament from "@/components/site/Ornament";

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el)
    window.scrollTo({
      top: el.getBoundingClientRect().top + window.scrollY - 70,
      behavior: "smooth",
    });
};

/* ----------------- HERO ----------------- */
const Hero = () => (
  <section
    id="hero"
    className="relative pt-32 md:pt-40 pb-24 md:pb-32 bg-[#F3EFE7] overflow-hidden"
    data-testid="section-hero"
  >
    {/* Background ornamental sparks */}
    <div className="pointer-events-none absolute -top-10 right-[8%] opacity-20">
      <Spark size={68} />
    </div>
    <div className="pointer-events-none absolute bottom-10 left-[6%] opacity-15">
      <Spark size={42} />
    </div>

    <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-10 lg:gap-10 items-center">
      <div className="lg:col-span-8 min-w-0">
        <Reveal>
          <SectionLabel testid="hero-eyebrow">The Intelligence Department</SectionLabel>
        </Reveal>
        <Reveal delay={120}>
          <h1
            className="font-display mt-7 text-[1.85rem] sm:text-3xl md:text-[2.4rem] lg:text-[3rem] xl:text-[3.6rem] leading-[1.05] text-[#2A2422] xl:whitespace-nowrap"
            data-testid="hero-headline"
          >
            Clarity Ignites <span style={{ fontStyle: "italic" }}>Momentum.</span>
          </h1>
        </Reveal>
        <Reveal delay={240}>
          <p
            className="mt-7 max-w-2xl font-body text-[1.05rem] md:text-lg text-[#50616B] leading-relaxed"
            data-testid="hero-subhead"
          >
            BOOMM Group helps creative micro and small businesses, founder-led
            organizations, creators, solopreneurs, and nonprofits turn scattered
            information into smarter decisions, stronger systems, and clearer next
            steps.
          </p>
        </Reveal>
        <Reveal delay={360}>
          <div className="mt-9 flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4">
            <button
              onClick={() => scrollTo("contact")}
              className="btn-primary px-7 py-4 text-sm font-medium tracking-wide rounded-[2px] inline-flex items-center justify-center gap-2"
              data-testid="hero-cta-primary"
            >
              Spark Better Decisions
              <ArrowRight size={16} />
            </button>
            <button
              onClick={() => scrollTo("framework")}
              className="btn-secondary px-7 py-4 text-sm font-medium tracking-wide rounded-[2px] inline-flex items-center justify-center gap-2"
              data-testid="hero-cta-secondary"
            >
              Explore POP&trade;
            </button>
          </div>
        </Reveal>
        <Reveal delay={480}>
          <div className="mt-12 flex flex-wrap items-center gap-x-5 gap-y-2 text-[#6E735B] font-body text-[0.68rem] sm:text-xs tracking-[0.22em] uppercase">
            <span>Intelligent</span>
            <span className="w-5 h-px bg-[#2A2422]/20" />
            <span>Strategic</span>
            <span className="w-5 h-px bg-[#2A2422]/20" />
            <span>Evidence&#8209;Informed</span>
            <span className="w-5 h-px bg-[#2A2422]/20 hidden sm:block" />
            <span className="hidden sm:inline">Actionable</span>
          </div>
        </Reveal>
      </div>

      {/* Hero monogram visual — clean card with just the B mark */}
      <Reveal delay={300} className="lg:col-span-4">
        <div className="relative mx-auto w-full max-w-md">
          <div className="absolute inset-0 -m-6 border border-[#2A2422]/10 rounded-[2px]" />
          <div className="relative bg-white p-6 md:p-8 rounded-[2px] border border-[#2A2422]/10 aspect-square flex items-center justify-center">
            <Monogram size={420} className="w-full h-full object-contain" />
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

/* ----------------- PROBLEM ----------------- */
const problems = [
  {
    n: "01",
    title: "Too many tools, not enough clarity",
    body: "Dashboards, AI assistants, marketing platforms — the stack grows, but the signal gets quieter.",
    icon: Layers,
  },
  {
    n: "02",
    title: "Customer preferences keep shifting",
    body: "What worked last quarter no longer maps to what your audience is actually looking for.",
    icon: Sparkles,
  },
  {
    n: "03",
    title: "Growth creates operational friction",
    body: "Founder time gets absorbed by repeat decisions and workflows that haven\u2019t kept up with the business.",
    icon: Workflow,
  },
];

const Problem = () => (
  <section
    id="problem"
    className="bg-white py-24 md:py-32 border-y border-[#2A2422]/8"
    data-testid="section-problem"
  >
    <div className="max-w-7xl mx-auto px-6 md:px-10">
      <Reveal>
        <SectionLabel testid="problem-eyebrow">The Idea</SectionLabel>
      </Reveal>
      <div className="mt-6 grid md:grid-cols-12 gap-10 md:gap-16">
        <Reveal delay={120} className="md:col-span-7">
          <h2 className="font-display text-3xl md:text-5xl text-[#2A2422] leading-tight">
            You do not need more noise.
            <br />
            <span className="text-[#6E735B]">You need clearer signals.</span>
          </h2>
        </Reveal>
        <Reveal delay={240} className="md:col-span-5">
          <p className="font-body text-[#50616B] text-[1.02rem] leading-relaxed">
            Small businesses are surrounded by disconnected tools, shifting customer
            behavior, AI pressure, inconsistent marketing, and operational friction.
            BOOMM helps make sense of what is happening, what matters, and what to do
            next.
          </p>
        </Reveal>
      </div>

      <div className="mt-14 grid md:grid-cols-3 gap-6 md:gap-8">
        {problems.map((p, i) => (
          <Reveal key={p.n} delay={i * 120}>
            <div className="card-edge p-8 md:p-10 rounded-[2px] h-full">
              <div className="flex items-center justify-between">
                <span className="eyebrow text-[#B58B42]">{p.n}</span>
                <p.icon size={20} className="text-[#6E735B]" />
              </div>
              <h3 className="mt-8 font-display text-xl md:text-2xl text-[#2A2422] leading-snug">
                {p.title}
              </h3>
              <p className="mt-3 font-body text-[#50616B] leading-relaxed text-[0.96rem]">
                {p.body}
              </p>
            </div>
          </Reveal>
        ))}
      </div>

      {/* Positioning callout — built for the budget-aware founder */}
      <Reveal delay={200}>
        <div
          className="mt-12 md:mt-16 grid md:grid-cols-12 gap-6 md:gap-10 items-center bg-[#2A2422] text-[#F3EFE7] rounded-[2px] p-8 md:p-12 border border-[#B58B42]/40"
          data-testid="problem-positioning"
        >
          <div className="md:col-span-3 flex md:flex-col items-start gap-3">
            <span className="eyebrow text-[#B58B42]">Built For</span>
            <h3 className="font-display text-2xl md:text-[1.85rem] leading-tight">
              The micro &amp; small business owner.
            </h3>
          </div>
          <div className="md:col-span-9">
            <p className="font-body text-[#F3EFE7]/85 text-[1.02rem] md:text-[1.08rem] leading-relaxed">
              Big businesses have analyst teams and intelligence budgets to support
              reports like these at scale.{" "}
              <span className="text-[#F3EFE7]">
                BOOMM is built specifically for creatives, micro and small
                businesses, founder-led organizations, creators, solopreneurs, and
                nonprofits
              </span>{" "}
              &mdash; so you can get competitive analysis, market intelligence, and
              decision support without busting your budget.
            </p>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

/* ----------------- SERVICES ----------------- */
const services = [
  {
    n: "I",
    title: "Business Optimization",
    body: "Improve operations, workflows, efficiency, customer experience, and strategic execution.",
    icon: Workflow,
  },
  {
    n: "II",
    title: "AI Strategy & Adoption",
    body: "Identify practical AI opportunities, build responsible workflows, and help teams use AI as a business tool.",
    icon: Cpu,
  },
  {
    n: "III",
    title: "Market Intelligence",
    body: "Analyze customers, competitors, industry trends, and search behavior to uncover opportunity signals.",
    icon: LineChart,
  },
  {
    n: "IV",
    title: "Executive Advisory",
    body: "Strategic guidance that helps leaders prioritize, decide, and move with confidence.",
    icon: ShieldCheck,
  },
];

const Services = () => (
  <section
    id="services"
    className="bg-[#F3EFE7] py-24 md:py-32 relative grain-bg"
    data-testid="section-services"
  >
    <div className="max-w-7xl mx-auto px-6 md:px-10 relative">
      <Reveal>
        <SectionLabel testid="services-eyebrow">The Work</SectionLabel>
      </Reveal>
      <Reveal delay={120}>
        <h2 className="mt-6 font-display text-3xl md:text-5xl text-[#2A2422] leading-tight max-w-3xl">
          The intelligence layer
          <br />
          between insight and action.
        </h2>
      </Reveal>

      <div className="mt-14 grid md:grid-cols-2 gap-6 md:gap-8">
        {services.map((s, i) => (
          <Reveal key={s.title} delay={i * 100}>
            <div className="card-edge p-8 md:p-12 rounded-[2px] h-full">
              <div className="flex items-start justify-between gap-6">
                <div className="flex items-center gap-4">
                  <span className="font-display text-[#B58B42] text-base tracking-widest">
                    {s.n}
                  </span>
                  <span className="w-10 h-px bg-[#B58B42]" />
                </div>
                <s.icon size={22} className="text-[#50616B]" />
              </div>
              <h3 className="mt-8 font-display text-2xl md:text-[1.75rem] text-[#2A2422] leading-snug">
                {s.title}
              </h3>
              <p className="mt-4 font-body text-[#50616B] leading-relaxed">
                {s.body}
              </p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

/* ----------------- POP FRAMEWORK ----------------- */
const popPhases = [
  {
    n: "Phase 1",
    title: "Discover",
    purpose: "Collect and organize market intelligence.",
    components: [
      {
        name: "Trend Radar\u2122",
        body: "Identifies emerging market, technology, consumer, economic, and industry trends that may influence future demand, business opportunities, and strategic priorities.",
      },
      {
        name: "Consumer Voice Analysis\u2122",
        body: "Synthesizes customer feedback, search behavior, reviews, social conversations, and market sentiment to uncover unmet needs, evolving preferences, and buying motivations.",
      },
      {
        name: "Competitive Signal Analysis\u2122",
        body: "Evaluates competitor positioning, pricing, innovation, messaging, partnerships, and market activity to identify competitive advantages, market gaps, and differentiation opportunities.",
      },
    ],
  },
  {
    n: "Phase 2",
    title: "Evaluate",
    purpose: "Transform evidence into executive decision intelligence.",
    components: [
      {
        name: "Evidence Profile\u2122",
        body: "Organizes research into standardized, evidence-based component scores that summarize market conditions and opportunity signals.",
      },
      {
        name: "Preference Strength Index\u2122 (PSI\u2122)",
        body: "Applies BOOMM\u2019s proprietary Decision Intelligence model to transform evidence into an executive-level assessment of market opportunity strength, strategic potential, and business readiness.",
      },
    ],
  },
  {
    n: "Phase 3",
    title: "Prioritize",
    purpose: "Determine what matters most and where to focus.",
    components: [
      {
        name: "Opportunity Compass\u2122",
        body: "Prioritizes and visualizes opportunities based on demand, profitability, feasibility, strategic alignment, and market conditions to guide confident business decisions and investment priorities.",
      },
      {
        name: "Opportunity Signals\u2122",
        body: "Monitors meaningful changes in customer behavior, competitive activity, technology adoption, and market dynamics to surface emerging opportunities and potential risks before they become obvious.",
      },
    ],
  },
  {
    n: "Phase 4",
    title: "Act",
    purpose: "Turn intelligence into measurable business action.",
    components: [
      {
        name: "Strategic Recommendations\u2122",
        body: "Transforms research and decision intelligence into prioritized, evidence-based action plans with practical recommendations, implementation guidance, and measurable business outcomes.",
      },
    ],
  },
];

const Framework = () => (
  <section
    id="framework"
    className="bg-white py-24 md:py-32 border-y border-[#2A2422]/8"
    data-testid="section-framework"
  >
    <div className="max-w-7xl mx-auto px-6 md:px-10">
      <Reveal>
        <SectionLabel testid="framework-eyebrow">The Framework</SectionLabel>
      </Reveal>
      <div className="mt-6 grid md:grid-cols-12 gap-10 md:gap-16">
        <Reveal delay={120} className="md:col-span-7">
          <h2 className="font-display text-3xl md:text-5xl text-[#2A2422] leading-tight">
            Meet POP&trade;:
            <br />
            <span className="text-[#B58B42]">Prediction-of-Preferences</span>
            <br />
            Intelligence.
          </h2>
        </Reveal>
        <Reveal delay={240} className="md:col-span-5">
          <p className="font-body text-[#50616B] text-[1.02rem] leading-relaxed">
            POP&trade; is BOOMM Group&apos;s proprietary decision intelligence framework
            for identifying market signals, customer preference shifts, competitive
            gaps, and emerging opportunities. It helps business owners answer the
            question: <span className="text-[#2A2422]">What should we do next?</span>
          </p>
          <button
            onClick={() => scrollTo("lead-magnet")}
            className="mt-6 inline-flex items-center gap-2 font-body text-sm text-[#2A2422] border-b border-[#B58B42] pb-1 hover:text-[#B58B42] transition-colors"
            data-testid="framework-cta"
          >
            Explore POP&trade;
            <ArrowRight size={14} />
          </button>
        </Reveal>
      </div>

      {/* Flow diagram */}
      <Reveal delay={300} className="mt-16">
        <div className="bg-[#FBF9F4] border border-[#2A2422]/8 rounded-[2px] p-8 md:p-12">
          <p className="eyebrow text-[#6E735B]">Signal Flow</p>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-7 gap-6 items-stretch">
            <div className="md:col-span-3 grid grid-cols-2 gap-3">
              {["Market Signals", "Customer Signals", "Competitive Signals", "Search Signals"].map(
                (s) => (
                  <div
                    key={s}
                    className="border border-[#2A2422]/12 rounded-[2px] px-4 py-4 bg-white text-center"
                  >
                    <Spark size={14} className="inline-block" />
                    <p className="mt-2 font-body text-sm text-[#2A2422]">{s}</p>
                  </div>
                )
              )}
            </div>
            <div className="md:col-span-1 flex flex-col items-center justify-center text-[#B58B42]">
              <div className="hidden md:block fuse-line w-full" />
              <ArrowRight className="md:hidden" />
              <p className="mt-3 eyebrow text-[#6E735B]">Synthesis</p>
            </div>
            <div className="md:col-span-3 grid grid-cols-1 gap-3">
              <div className="border border-[#2A2422]/12 rounded-[2px] px-5 py-4 bg-white">
                <p className="eyebrow text-[#B58B42]">Step 1</p>
                <p className="mt-1 font-display text-lg text-[#2A2422]">Opportunity Insight</p>
              </div>
              <div className="border border-[#2A2422]/12 rounded-[2px] px-5 py-4 bg-white">
                <p className="eyebrow text-[#B58B42]">Step 2</p>
                <p className="mt-1 font-display text-lg text-[#2A2422]">Strategic Recommendation</p>
              </div>
              <div className="border border-[#B58B42] rounded-[2px] px-5 py-4 bg-[#2A2422] text-[#F3EFE7]">
                <p className="eyebrow text-[#B58B42]">Step 3</p>
                <p className="mt-1 font-display text-lg">Business Action</p>
              </div>
            </div>
          </div>
        </div>
      </Reveal>

      {/* Phases + components */}
      <Reveal delay={200} className="mt-20">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <p className="eyebrow text-[#6E735B]">POP&trade; Components</p>
            <h3 className="mt-3 font-display text-2xl md:text-[2rem] text-[#2A2422] leading-tight max-w-2xl">
              Four phases, eight components.
            </h3>
          </div>
          <p className="font-body text-sm text-[#50616B] max-w-sm">
            From discovery to action &mdash; an intentional flow that turns scattered
            evidence into a clear next step.
          </p>
        </div>

        <div className="mt-10 space-y-8" data-testid="pop-phases">
          {popPhases.map((phase, pi) => (
            <div
              key={phase.title}
              className="grid md:grid-cols-12 gap-6 md:gap-10 items-start"
              data-testid={`pop-phase-${pi + 1}`}
            >
              <div className="md:col-span-3">
                <div className="flex items-center gap-3">
                  <span className="font-display text-[#B58B42] text-sm tracking-[0.24em]">
                    {phase.n.toUpperCase()}
                  </span>
                  <span className="w-8 h-px bg-[#B58B42]" />
                </div>
                <h4 className="mt-3 font-display text-[1.7rem] md:text-[2rem] text-[#2A2422] leading-tight">
                  {phase.title}
                </h4>
                <p className="mt-2 font-body text-sm text-[#50616B] italic leading-relaxed">
                  &ldquo;{phase.purpose}&rdquo;
                </p>
              </div>
              <div
                className={`md:col-span-9 grid gap-4 ${
                  phase.components.length === 3
                    ? "md:grid-cols-3"
                    : phase.components.length === 2
                    ? "md:grid-cols-2"
                    : "md:grid-cols-1"
                }`}
              >
                {phase.components.map((c) => (
                  <div
                    key={c.name}
                    className="card-edge p-6 md:p-7 rounded-[2px] h-full bg-[#FBF9F4]"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <Spark size={14} />
                      <span className="eyebrow text-[#B58B42] text-[0.62rem]">
                        POP&trade;
                      </span>
                    </div>
                    <p className="mt-5 font-display text-[1.05rem] md:text-[1.15rem] text-[#2A2422] leading-snug">
                      {c.name}
                    </p>
                    <p className="mt-3 font-body text-[0.88rem] text-[#50616B] leading-relaxed">
                      {c.body}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Reveal>

      {/* Engagement options */}
      <Reveal delay={200} className="mt-16">
        <div
          className="bg-[#FBF9F4] border border-[#2A2422]/10 rounded-[2px] p-8 md:p-12 grid md:grid-cols-12 gap-8 md:gap-12 items-start"
          data-testid="framework-engagement"
        >
          <div className="md:col-span-4">
            <p className="eyebrow text-[#B58B42]">How To Engage</p>
            <h3 className="mt-3 font-display text-2xl md:text-[2rem] text-[#2A2422] leading-tight">
              Right-sized intelligence for the work you&apos;re doing.
            </h3>
            <p className="mt-4 font-body text-sm text-[#50616B] leading-relaxed">
              BOOMM can deliver a report for a single component, a single phase, the
              full framework once, or a quarterly retainer &mdash; whatever fits the
              decision you&apos;re trying to make. We build what you actually need and
              create a pacing guide that works inside your timeframe and budget.
            </p>
          </div>
          <div className="md:col-span-8 grid sm:grid-cols-2 gap-4">
            {[
              {
                t: "One Component",
                b: "Targeted intelligence on a single POP\u2122 component \u2014 useful when one question is in the way.",
              },
              {
                t: "One Phase",
                b: "Discover, Evaluate, Prioritize, or Act \u2014 delivered as a focused engagement.",
              },
              {
                t: "Full Brief (One-Time)",
                b: "The complete POP\u2122 framework applied to your business or category, end to end.",
              },
              {
                t: "Quarterly Retainer",
                b: "Ongoing decision intelligence with refreshed signals, scoring, and recommendations.",
              },
            ].map((o) => (
              <div
                key={o.t}
                className="card-edge p-5 md:p-6 rounded-[2px] h-full"
              >
                <div className="flex items-center gap-3">
                  <Spark size={14} />
                  <p className="font-display text-[1.05rem] text-[#2A2422]">
                    {o.t}
                  </p>
                </div>
                <p className="mt-2 font-body text-[0.88rem] text-[#50616B] leading-relaxed">
                  {o.b}
                </p>
              </div>
            ))}
            <div className="sm:col-span-2 mt-1 flex items-start gap-3 font-body text-sm text-[#50616B] leading-relaxed">
              <Spark size={14} className="mt-1 shrink-0" />
              <span>
                Need something different? We&apos;ll build a custom scope and a
                pacing guide that fits your timeframe and budget.
              </span>
            </div>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);
const previewReports = [
  {
    id: "beauty_report",
    eyebrow: "Industry Brief",
    title: "Beauty \u2014 Opportunity Brief",
    body: "Where beauty buyers are heading next \u2014 signals across ingredients, ritual, and channel preference.",
    file: "/brand/BOOMM_Beauty_Report.pdf",
  },
  {
    id: "spa_report",
    eyebrow: "Industry Brief",
    title: "Spa \u2014 Opportunity Brief",
    body: "How spa demand is reorganizing around recovery, longevity, and member-based models.",
    file: "/brand/BOOMM_Spa_Report.pdf",
  },
  {
    id: "beauty_infographic",
    eyebrow: "Visual Summary",
    title: "Beauty \u2014 Infographic Snapshot",
    body: "A one-page visual of the beauty brief\u2019s sharpest signals.",
    file: "/brand/BOOMM_Beauty_Infographic.pdf",
  },
  {
    id: "spa_infographic",
    eyebrow: "Visual Summary",
    title: "Spa \u2014 Infographic Snapshot",
    body: "A one-page visual of the spa brief\u2019s sharpest signals.",
    file: "/brand/BOOMM_Spa_Infographic.pdf",
  },
];

const LeadMagnet = () => (
  <section
    id="lead-magnet"
    className="bg-[#F3EFE7] py-24 md:py-32"
    data-testid="section-lead-magnet"
  >
    <div className="max-w-7xl mx-auto px-6 md:px-10">
      <Reveal>
        <SectionLabel testid="lead-eyebrow">The Signal</SectionLabel>
      </Reveal>
      <Reveal delay={120}>
        <h2 className="mt-6 font-display text-3xl md:text-5xl text-[#2A2422] leading-tight max-w-3xl">
          See what clearer decision
          <br />
          intelligence looks like.
        </h2>
      </Reveal>
      <Reveal delay={240}>
        <p className="mt-5 max-w-2xl font-body text-[#50616B] leading-relaxed">
          Choose a sample BOOMM Opportunity Brief and see how fragmented signals
          become practical recommendations. Two industries, two formats &mdash;
          pick the one closest to your work.
        </p>
      </Reveal>

      <div className="mt-14 grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
        {/* Form (left) */}
        <Reveal delay={120} className="lg:col-span-7">
          <LeadForm />
        </Reveal>

        {/* Sample report tiles (right) */}
        <Reveal delay={240} className="lg:col-span-5">
          <div className="bg-white border border-[#2A2422]/10 rounded-[2px] p-7 md:p-9">
            <p className="eyebrow text-[#B58B42]">Available Briefs</p>
            <h3 className="mt-3 font-display text-2xl text-[#2A2422]">
              Four sample briefs to choose from.
            </h3>
            <p className="mt-2 font-body text-sm text-[#50616B] leading-relaxed">
              These are real examples of how BOOMM synthesizes signals into a clear
              recommendation set. Share a few details on the form and you can
              download the brief that&apos;s closest to your work.
            </p>
            <div className="mt-6 space-y-3" data-testid="sample-reports-list">
              {previewReports.map((r) => (
                <div
                  key={r.id}
                  className="border-l border-[#B58B42] pl-4 py-1"
                  data-testid={`sample-report-${r.id}`}
                >
                  <p className="eyebrow text-[#B58B42]">{r.eyebrow}</p>
                  <p className="font-display text-[1.02rem] text-[#2A2422] mt-1">
                    {r.title}
                  </p>
                  <p className="font-body text-sm text-[#50616B] mt-1 leading-relaxed">
                    {r.body}
                  </p>
                </div>
              ))}
            </div>
            <div className="mt-6 pt-5 border-t border-[#2A2422]/10 text-xs font-body text-[#6E735B]">
              Internal methodology, scoring logic, and analyst processes are
              intentionally not shown.
            </div>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

/* ----------------- AUDIENCE ----------------- */
const audience = [
  "Creatives",
  "Micro and small businesses",
  "Solopreneurs &amp; consultants",
  "Founder-led service businesses",
  "Coaches &amp; creators",
  "Wellness, professional services, home services",
  "Education &amp; training businesses",
  "Nonprofits &amp; mission-driven organizations",
  "Growing organizations needing clearer systems",
];

const Audience = () => (
  <section
    id="audience"
    className="bg-white py-24 md:py-32 border-y border-[#2A2422]/8 relative"
    data-testid="section-audience"
  >
    <div className="absolute inset-0 dot-grid opacity-60 pointer-events-none" aria-hidden="true" />
    <div className="max-w-7xl mx-auto px-6 md:px-10 relative">
      <Reveal>
        <SectionLabel testid="audience-eyebrow">Who BOOMM Helps</SectionLabel>
      </Reveal>
      <Reveal delay={120}>
        <h2 className="mt-6 font-display text-3xl md:text-5xl text-[#2A2422] leading-tight max-w-3xl">
          Built for businesses with momentum,
          <br />
          complexity, and untapped opportunity.
        </h2>
      </Reveal>

      <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {audience.map((a, i) => (
          <Reveal key={a} delay={i * 80}>
            <div className="border border-[#2A2422]/10 px-6 py-7 rounded-[2px] bg-[#FBF9F4] hover:border-[#B58B42] transition-colors h-full">
              <div className="flex items-start gap-3">
                <Spark size={14} className="mt-1" />
                <p
                  className="font-body text-[#2A2422] text-[0.98rem] leading-snug"
                  dangerouslySetInnerHTML={{ __html: a }}
                />
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

/* ----------------- WHY BOOMM ----------------- */
const whyPoints = [
  "Clearer decision-making",
  "Better use of AI",
  "Stronger operational systems",
  "More focused growth priorities",
  "Actionable intelligence, not raw research",
  "Strategy that connects to execution",
];

const Why = () => (
  <section
    id="why"
    className="bg-[#F3EFE7] py-24 md:py-32 relative grain-bg"
    data-testid="section-why"
  >
    <div className="max-w-7xl mx-auto px-6 md:px-10 relative grid md:grid-cols-12 gap-12 md:gap-16">
      <div className="md:col-span-5">
        <Reveal>
          <SectionLabel testid="why-eyebrow">Why BOOMM</SectionLabel>
        </Reveal>
        <Reveal delay={120}>
          <h2 className="mt-6 font-display text-3xl md:text-[2.6rem] leading-tight text-[#2A2422]">
            Practical strategy.
            <br />
            Human judgment.
            <br />
            <span className="text-[#B58B42]">AI-enabled intelligence.</span>
          </h2>
        </Reveal>
        <Reveal delay={240}>
          <p className="mt-6 font-body text-[#50616B] leading-relaxed">
            BOOMM combines strategic planning, operational optimization, marketing
            insight, AI-enabled workflows, and market intelligence to help clients
            build businesses that are clearer, stronger, and more sustainable.
          </p>
        </Reveal>
      </div>
      <div className="md:col-span-7">
        <Reveal delay={120}>
          <ul className="space-y-4 md:space-y-5">
            {whyPoints.map((w, i) => (
              <li
                key={w}
                className="flex items-start gap-4 border-b border-[#2A2422]/10 pb-4"
              >
                <div className="mt-1">
                  <Spark size={16} />
                </div>
                <div>
                  <span className="font-display text-[#2A2422] text-lg md:text-xl">
                    {w}
                  </span>
                </div>
                <span className="ml-auto eyebrow text-[#B58B42]">
                  {String(i + 1).padStart(2, "0")}
                </span>
              </li>
            ))}
          </ul>
        </Reveal>
      </div>
    </div>
  </section>
);

/* ----------------- ABOUT ----------------- */
const About = () => (
  <section
    id="about"
    className="bg-white py-24 md:py-32 border-y border-[#2A2422]/8"
    data-testid="section-about"
  >
    <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
      <Reveal className="lg:col-span-5">
        <div className="relative">
          <div className="aspect-[4/5] bg-[#2A2422] rounded-[2px] overflow-hidden relative">
            <img
              src="/mj-mauney.jpg"
              alt="MJ Mauney, Founder and Principal Strategist of BOOMM Group"
              className="absolute inset-0 w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#2A2422]/55 via-transparent to-transparent" />
            <div className="absolute bottom-6 left-6 text-[#F3EFE7]">
              <p className="eyebrow text-[#B58B42]">Founder &amp; Principal Strategist</p>
              <p className="font-display text-2xl mt-1">MJ Mauney</p>
            </div>
          </div>
          <div className="absolute -bottom-6 -right-6 bg-[#F3EFE7] border border-[#2A2422]/10 p-5 rounded-[2px] hidden md:block">
            <Monogram size={56} />
          </div>
        </div>
      </Reveal>

      <div className="lg:col-span-7">
        <Reveal>
          <SectionLabel testid="about-eyebrow">The Founder</SectionLabel>
        </Reveal>
        <Reveal delay={120}>
          <h2 className="mt-6 font-display text-3xl md:text-5xl text-[#2A2422] leading-tight">
            Founded by MJ Mauney.
          </h2>
        </Reveal>
        <Reveal delay={240}>
          <p className="mt-6 font-body text-[#50616B] leading-relaxed">
            MJ Mauney is the Founder and Principal Strategist of BOOMM Group. She
            brings more than two decades of experience across business development,
            workforce strategy, higher education, recruitment marketing, brand
            development, and organizational operations. Her work focuses on helping
            organizations simplify complexity, strengthen systems, and move from
            potential to performance.
          </p>
        </Reveal>
        <Reveal delay={360}>
          <div className="mt-8 bg-[#FBF9F4] border-l-2 border-[#B58B42] pl-6 py-5">
            <Quote size={18} className="text-[#B58B42]" />
            <p className="mt-3 font-display text-xl md:text-2xl text-[#2A2422] leading-snug">
              Lasting transformation begins with clarity.
            </p>
          </div>
        </Reveal>
        <Reveal delay={480}>
          <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
            {[
              "20+ years across business development &amp; workforce strategy",
              "Operational optimization &amp; recruitment marketing expertise",
              "AI-enabled workflow design for founder-led teams",
              "Currently pursuing an MFA in Media Design",
            ].map((h) => (
              <li
                key={h}
                className="flex items-start gap-3 font-body text-sm text-[#50616B]"
              >
                <Spark size={12} className="mt-1.5" />
                <span dangerouslySetInnerHTML={{ __html: h }} />
              </li>
            ))}
          </ul>
        </Reveal>
        <Reveal delay={600}>
          <button
            onClick={() => scrollTo("contact")}
            className="mt-10 btn-primary px-7 py-4 text-sm font-medium tracking-wide rounded-[2px] inline-flex items-center gap-2"
            data-testid="about-cta"
          >
            Work With BOOMM <ArrowRight size={16} />
          </button>
        </Reveal>
      </div>
    </div>
  </section>
);

/* ----------------- CONTACT ----------------- */
const Contact = () => (
  <section
    id="contact"
    className="bg-[#F3EFE7] py-24 md:py-32"
    data-testid="section-contact"
  >
    <div className="max-w-5xl mx-auto px-6 md:px-10">
      <Reveal>
        <SectionLabel testid="contact-eyebrow">Contact</SectionLabel>
      </Reveal>
      <Reveal delay={120}>
        <h2 className="mt-6 font-display text-3xl md:text-5xl text-[#2A2422] leading-tight">
          Ready to spark better decisions?
        </h2>
      </Reveal>
      <Reveal delay={240}>
        <p className="mt-5 max-w-2xl font-body text-[#50616B] leading-relaxed">
          Tell BOOMM where your business feels unclear, overloaded, or ready for its
          next level of momentum.
        </p>
      </Reveal>
      <Reveal delay={360} className="mt-12">
        <ContactForm />
      </Reveal>
    </div>
  </section>
);

/* ----------------- LANDING ----------------- */
const Landing = () => {
  return (
    <div className="bg-[#F3EFE7]" data-testid="landing-page">
      <Nav />
      <main>
        <Hero />
        <Problem />
        <PullQuote
          testid="pullquote-problem"
          tone="linen"
          eyebrow="The Idea"
          quote="You cannot scale clarity by adding more dashboards. You scale it with better questions."
        />
        <Services />
        <EditorialBreak
          testid="editorial-services"
          image="https://images.unsplash.com/photo-1517842645767-c639042777db?auto=format&fit=crop&w=2000&q=80"
          eyebrow="The Practice"
          headline="Strategy is a craft. Intelligence is its instrument."
          subline={"BOOMM brings the focus of an analyst, the judgment of an advisor, and the rhythm of a thinking partner \u2014 all sized for founder-led businesses."}
        />
        <Framework />
        <StatsStrip
          testid="stats-strip"
          items={[
            { value: "4", label: "POP\u2122 Phases" },
            { value: "8", label: "POP\u2122 Components" },
            { value: "1", label: "Clear Next Step" },
          ]}
        />
        <LeadMagnet />
        <Audience />
        <PullQuote
          testid="pullquote-philosophy"
          tone="espresso"
          eyebrow="The Philosophy"
          quote="Lasting transformation begins with clarity."
          attribution="MJ Mauney, Founder"
        />
        <Why />
        <About />
        <Contact />
      </main>
      <Footer />
    </div>
  );
};

export default Landing;
